/**
 * ConnectShopifyWizard — per-merchant Dev Dashboard custom-app
 * onboarding (W.2 pivot, 2026-04-28).
 *
 * Each merchant creates their OWN Shopify Partner account + custom app
 * and pastes the resulting `client_id` + `client_secret` into Studio.
 * The orchestrator's `/v1/shopify/connect` endpoint encrypts the
 * secret, persists a pending OAuth state row, and returns the install
 * URL we redirect into.
 *
 * Why a 4-step wizard instead of the legacy single-input
 * `ShopifyConnectModal`: setting up a custom app in the Partner
 * Dashboard is non-trivial — the merchant has to configure scopes,
 * a redirect URL, and Privacy/GDPR webhook subscriptions. Stepping
 * them through with copy-paste values reduces support load
 * dramatically. See the plan W.3 for the full rationale.
 *
 * Step layout:
 *   1. Explainer + screencast pointer
 *   2. Partner Dashboard setup walkthrough (with copy-paste config)
 *   3. Paste credentials + shop domain
 *   4. Submit + redirect to OAuth
 *
 * Step 5 of the plan (auto-install loader toggle) lives on the Sources
 * page after OAuth completes — not in this wizard.
 */

import { type FormEvent, useState } from "react";
import { ArrowLeft, ArrowRight, ExternalLink, X, Copy, CheckCircle2 } from "lucide-react";
import { getApiClient } from "../../lib/apiClient";
import { ApiError } from "../../types/api";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where to land after OAuth completes. Defaults to /sources. */
  returnTo?: string;
}

const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i;

// The exact scope list our orchestrator requires. Must match the
// orchestrator's expected scopes — Shopify rejects OAuth callbacks
// where granted scopes are a strict subset.
const REQUIRED_SCOPES = [
  "read_customers",
  "read_discounts",
  "read_fulfillments",
  "read_inventory",
  "read_locales",
  "read_locations",
  "read_orders",
  "write_orders",
  "read_products",
  "read_shipping",
  "write_draft_orders",
  "write_themes",
];

const ORCHESTRATOR_BASE = (() => {
  const env = (import.meta as { env?: Record<string, string> }).env;
  return env?.VITE_API_BASE_URL ?? "https://checkout-api.costplus.io";
})();

// Privacy/GDPR webhooks the merchant must configure in their Dev
// Dashboard custom-app config. Operational webhooks
// (orders/paid, refunds/create, etc.) are registered programmatically
// after OAuth — those are NOT in this list.
const GDPR_WEBHOOKS = [
  { topic: "customers/data_request", description: "GDPR data-export request" },
  { topic: "customers/redact", description: "GDPR customer-data deletion" },
  { topic: "shop/redact", description: "GDPR shop-data deletion" },
];

export function ConnectShopifyWizard({ open, onClose, returnTo }: Props) {
  const [step, setStep] = useState(1);
  const [shop, setShop] = useState("");
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  function reset() {
    setStep(1);
    setShop("");
    setClientID("");
    setClientSecret("");
    setError(null);
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    let normalized = shop.trim().toLowerCase();
    if (normalized && !normalized.includes(".")) {
      normalized = `${normalized}.myshopify.com`;
    }
    if (!SHOP_PATTERN.test(normalized)) {
      setError("Shop domain must be a valid .myshopify.com address.");
      return;
    }
    if (!clientID.trim() || !clientSecret.trim()) {
      setError("Both client_id and client_secret are required.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await getApiClient().shopify.connect({
        shop_domain: normalized,
        client_id: clientID.trim(),
        client_secret: clientSecret.trim(),
        return_to: returnTo ?? "/sources",
      });
      if (!result.install_url || result.install_url.startsWith("#")) {
        setError("Connect Shopify isn't available in offline Studio mode.");
        setSubmitting(false);
        return;
      }
      // Full-page redirect — Shopify needs the merchant's browser for
      // the consent screen, then bounces back to our /v1/shopify/callback.
      window.location.assign(result.install_url);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to start the install. Try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg bg-white dark:bg-neutral-950 shadow-xl border border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <Header step={step} onClose={handleClose} />

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 ? <Step1Explainer /> : null}
          {step === 2 ? <Step2PartnerDashboard /> : null}
          {step === 3 ? (
            <Step3Credentials
              shop={shop}
              setShop={setShop}
              clientID={clientID}
              setClientID={setClientID}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              error={error}
            />
          ) : null}
          {step === 4 ? (
            <Step4Submit
              shop={shop}
              clientID={clientID}
              error={error}
              submitting={submitting}
            />
          ) : null}
        </div>

        <Footer
          step={step}
          submitting={submitting}
          canAdvance={
            step === 3
              ? Boolean(shop.trim() && clientID.trim() && clientSecret.trim())
              : true
          }
          onBack={() => setStep((s) => Math.max(1, s - 1))}
          onNext={() => setStep((s) => Math.min(4, s + 1))}
          onSubmit={onSubmit}
        />
      </div>
    </div>
  );
}

// ─── Header / Footer chrome ──────────────────────────────────────────────────

function Header({ step, onClose }: { step: number; onClose: () => void }) {
  return (
    <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
      <div>
        <h2 className="text-base font-semibold">Connect a Shopify store</h2>
        <p className="text-xs text-neutral-500 mt-0.5">
          Step {step} of 4
          {" · "}
          {step === 1 ? "Overview" : null}
          {step === 2 ? "Set up in Shopify Partner Dashboard" : null}
          {step === 3 ? "Paste your app's credentials" : null}
          {step === 4 ? "Confirm and install" : null}
        </p>
      </div>
      <button
        onClick={onClose}
        className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function Footer(props: {
  step: number;
  submitting: boolean;
  canAdvance: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const { step, submitting, canAdvance, onBack, onNext, onSubmit } = props;
  return (
    <div className="px-6 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
      {step > 1 ? (
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
      ) : (
        <span />
      )}
      {step < 4 ? (
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          className="inline-flex items-center gap-1 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-xs font-medium hover:bg-category-trigger/20 disabled:opacity-50"
        >
          Next
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => onSubmit(e as unknown as FormEvent)}
          disabled={submitting || !canAdvance}
          className="inline-flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-xs font-medium hover:bg-category-trigger/20 disabled:opacity-50"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {submitting ? "Redirecting…" : "Continue to Shopify"}
        </button>
      )}
    </div>
  );
}

// ─── Step 1: Explainer ───────────────────────────────────────────────────────

function Step1Explainer() {
  return (
    <div className="space-y-4 text-sm leading-6">
      <p>
        Cost+ Checkout integrates with your Shopify store via a custom app you
        create in your own Shopify Partner Dashboard. This takes about 10
        minutes.
      </p>
      <div className="rounded-md border border-neutral-200 dark:border-neutral-800 p-3 bg-neutral-50 dark:bg-neutral-900/40 text-xs space-y-2">
        <p className="font-medium">Why a custom app?</p>
        <p className="text-neutral-600 dark:text-neutral-400">
          Cost+ is a checkout-flow tool that runs alongside Shopify. We don't
          go through the public Shopify App Store. Your custom app gives you
          full control over what we can access on your store — and you can
          revoke it at any time directly from your Shopify admin.
        </p>
      </div>
      <p>You'll need:</p>
      <ul className="list-disc pl-5 space-y-1 text-neutral-700 dark:text-neutral-300">
        <li>
          A Shopify Partner account (free, sign up at{" "}
          <a
            href="https://partners.shopify.com/signup"
            target="_blank"
            rel="noreferrer"
            className="text-category-trigger underline"
          >
            partners.shopify.com/signup
          </a>
          )
        </li>
        <li>Your store's .myshopify.com domain</li>
        <li>About 10 minutes to copy/paste config values</li>
      </ul>
    </div>
  );
}

// ─── Step 2: Partner Dashboard walkthrough ───────────────────────────────────

function Step2PartnerDashboard() {
  return (
    <div className="space-y-5 text-sm">
      <p>
        Sign in to your{" "}
        <a
          href="https://partners.shopify.com/"
          target="_blank"
          rel="noreferrer"
          className="text-category-trigger underline"
        >
          Shopify Partners
        </a>{" "}
        account, then:
      </p>

      <Numbered n={1}>
        <span className="font-medium">Apps → Create app → Custom</span>
        <p className="text-xs text-neutral-500 mt-1">
          Name it anything — e.g. "Cost+ Checkout".
        </p>
      </Numbered>

      <Numbered n={2}>
        <span className="font-medium">Configure app URLs</span>
        <ConfigRow
          label="App URL"
          value="https://studio.costplus.io/sources"
        />
        <ConfigRow
          label="Allowed redirection URL"
          value={`${ORCHESTRATOR_BASE}/v1/shopify/callback`}
        />
      </Numbered>

      <Numbered n={3}>
        <span className="font-medium">Configure Admin API access scopes</span>
        <p className="text-xs text-neutral-500 mt-1 mb-2">
          Tick exactly these {REQUIRED_SCOPES.length} scopes:
        </p>
        <ConfigRow value={REQUIRED_SCOPES.join(",")} multiline />
      </Numbered>

      <Numbered n={4}>
        <span className="font-medium">
          Configure Privacy compliance (GDPR) webhooks
        </span>
        <p className="text-xs text-neutral-500 mt-1 mb-2">
          Add these three webhook subscriptions in the Privacy section. All
          three point to the same endpoint.
        </p>
        <ConfigRow
          label="Endpoint URL"
          value={`${ORCHESTRATOR_BASE}/v1/shopify/webhooks`}
        />
        <div className="mt-2 space-y-1 text-xs">
          {GDPR_WEBHOOKS.map((wh) => (
            <div
              key={wh.topic}
              className="flex items-center justify-between rounded border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5"
            >
              <code className="font-mono">{wh.topic}</code>
              <span className="text-neutral-500">{wh.description}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Other webhooks (orders, refunds, fulfillment, app/uninstalled) are
          registered automatically after install — you don't need to add
          them here.
        </p>
      </Numbered>

      <Numbered n={5}>
        <span className="font-medium">
          Click <span className="font-mono">API credentials</span> in the
          sidebar
        </span>
        <p className="text-xs text-neutral-500 mt-1">
          Copy the <span className="font-mono">Client ID</span> and{" "}
          <span className="font-mono">Client secret</span>. You'll paste them
          on the next step.
        </p>
      </Numbered>
    </div>
  );
}

function Numbered({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-category-trigger/10 text-category-trigger flex items-center justify-center text-xs font-semibold">
        {n}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function ConfigRow({
  label,
  value,
  multiline,
}: {
  label?: string;
  value: string;
  multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2">
      {label ? (
        <div className="text-[11px] font-medium text-neutral-500 mb-1">
          {label}
        </div>
      ) : null}
      <div className="flex items-start gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-2.5 py-1.5">
        <code
          className={`flex-1 font-mono text-xs ${multiline ? "break-all" : "truncate"}`}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="flex-shrink-0 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          aria-label="Copy"
        >
          {copied ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-category-trigger" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Credentials ─────────────────────────────────────────────────────

function Step3Credentials(props: {
  shop: string;
  setShop: (v: string) => void;
  clientID: string;
  setClientID: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  error: string | null;
}) {
  const { shop, setShop, clientID, setClientID, clientSecret, setClientSecret, error } = props;
  return (
    <div className="space-y-4">
      <p className="text-sm">
        Paste the values from your custom app. The client_secret is encrypted
        before it's stored — Cost+ staff can't read it.
      </p>

      <label className="block">
        <span className="text-xs font-medium">Shop domain</span>
        <span className="block text-[11px] text-neutral-500 mt-0.5">
          The .myshopify.com domain (not your custom domain).
        </span>
        <input
          type="text"
          value={shop}
          onChange={(e) => setShop(e.target.value)}
          placeholder="mystore.myshopify.com"
          className="mt-1 w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
          autoFocus
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium">Client ID</span>
        <input
          type="text"
          value={clientID}
          onChange={(e) => setClientID(e.target.value)}
          placeholder="32-character hex string"
          className="mt-1 w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium">Client secret</span>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder="shpss_… or hex string"
          className="mt-1 w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
          autoComplete="off"
        />
      </label>

      {error ? (
        <p className="text-xs text-category-error border border-category-error/30 bg-category-error/5 rounded-md px-2.5 py-2">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ─── Step 4: Submit ──────────────────────────────────────────────────────────

function Step4Submit({
  shop,
  clientID,
  error,
  submitting,
}: {
  shop: string;
  clientID: string;
  error: string | null;
  submitting: boolean;
}) {
  return (
    <div className="space-y-4 text-sm">
      <p>You're about to install Cost+ Checkout on:</p>
      <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-3 py-2 space-y-1 text-xs">
        <div>
          <span className="text-neutral-500">Shop:</span>{" "}
          <code className="font-mono">{shop || "—"}</code>
        </div>
        <div>
          <span className="text-neutral-500">Client ID:</span>{" "}
          <code className="font-mono">
            {clientID ? `${clientID.slice(0, 6)}…${clientID.slice(-4)}` : "—"}
          </code>
        </div>
      </div>
      <p>
        Click <strong>Continue to Shopify</strong>. Shopify will ask you to
        confirm the install on your store, then send you back here.
      </p>
      <p className="text-xs text-neutral-500">
        If anything went wrong with the custom app config, Shopify will show
        an error on its consent page rather than redirect back. You can come
        back and re-run this wizard at any time.
      </p>
      {error ? (
        <p className="text-xs text-category-error border border-category-error/30 bg-category-error/5 rounded-md px-2.5 py-2">
          {error}
        </p>
      ) : null}
      {submitting ? (
        <p className="text-xs text-neutral-500">
          Redirecting to Shopify…
        </p>
      ) : null}
    </div>
  );
}
